import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams } from "react-router-dom"
import { FaBars } from 'react-icons/fa'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import { apiURL, filesURL, sitePath } from 'config/globals'
import Topbar from 'components/Topbar'
import Sidebar from 'components/Sidebar'
import Breadcrumbs from 'components/Breadcrumbs'
import FormRow from 'components/FormRow'
import ModelViewer3d from "components/ModelViewer3d"
import CancelButton from 'components/CancelButton'
import SaveButton from 'components/SaveButton'
import Footer from 'components/Footer'
import { checkAuth } from 'utils/Auth'
import ReactImagePickerEditor from 'react-image-picker-editor'
import 'react-image-picker-editor/dist/index.css'

const BuildingNew = () => {
    const [currentUser, setCurrentUser] = useState(false)
    const [building, setBuilding] = useState({})
    const [form, setForm] = useState({
        newModelType: '3d'
    })
    const [hasFormChanged, setHasFormChanged] = useState(false)
    const [changeModel, setChangeModel] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const navigate = useNavigate()
    const { buildingId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            getBuilding(buildingId)
        }
        start()
    }, [])

    useEffect(() => {
        if (!building) {
            return
        }

        setForm({
            ...form,
            modelType: building.modelType
        })
    }, [building])

    const getBuilding = async (id) => {
        const response = await axios.get(`${apiURL}/get-building/${id}`)

        console.log(response)

        if (!response.data.success) {
            setErrorMessage(response.data.error || response.data)
            return
        }

        setBuilding(response.data.item)
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
        setHasFormChanged(true)
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        setIsSubmitting(true)
        
        try {
            const response = await axios.put(`${apiURL}/update-building/${buildingId}`, form)
            
            if (!response.data.success) {
                setErrorMessage(response.data.error || response.data)
                setIsSubmitting(false)
                return
            }

            navigate(`/buildings`)
        } catch (error) {
            setErrorMessage(JSON.stringify(error))
            setIsSubmitting(false)
        }
    }

    const cancel = () => {
        navigate(`/buildings/${building.id}`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <Sidebar selectedItem="buildings" currentUser={currentUser} />
            <main>
                <Breadcrumbs items={[['Buildings', '/buildings'], [building.name, `/buildings/${building.id}`], ['Edit']]} />
                <div className="btn-toggle">
                    <FaBars />
                </div>
                <header>
                    <div className="float-start">
                        <h1>Edit Building</h1>
                    </div>
                </header>
                {building &&
                    <>
                        <h3 className="mb-2">Archive information</h3>
                        <p className="mb-4">
                            Overall information about the building. Usually, information that does not change along the time. 
                        </p>
                        <FormRow
                            key="buildingName"
                            label="Building Name"
                            required={true}
                            content={
                                <Form.Control
                                    name="name"
                                    defaultValue={building.name}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="buildingDescription"
                            label="Description"
                            required={true}
                            content={
                                <Form.Control
                                    name="description"
                                    as="textarea"
                                    rows={10}
                                    defaultValue={building.description}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="inventoryNumber"
                            label="Inventory Number"
                            required={true}
                            content={
                                <Form.Control
                                    name="inventoryNumber"
                                    defaultValue={building.inventoryNumber}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="mainImage"
                            label="Main Image"
                            hint={<>Accepted types: JPG or PNG<br/>Recommended size: 1200x800</>}
                            required={true}
                            content={
                                <div className="mb-3">
                                    <ReactImagePickerEditor
                                        config={{
                                            borderRadius: '8px',
                                            language: 'en',
                                            width: '330px',
                                            height: '250px',
                                            objectFit: 'contain',
                                            compressInitial: null,
                                        }}
                                        imageSrcProp={building.mainImage
                                            ? `${filesURL}/${building.mainImage}`
                                            : null
                                        }
                                        imageChanged={(dataUri) => {
                                            handleChange('mainImage', {
                                                uri: dataUri
                                            })
                                        }}
                                    />
                                </div>
                            }
                        />
                        <h3 className="my-4 mb-2">Documentation model</h3>
                        <p className="mb-4">
                            This model is used for adding documentation records to the building. Please choose a 3D model or an image.
                        </p>
                        <FormRow
                            key="buildingModelType"
                            label="Current Model Type"
                            content={
                                <div style={{ lineHeight: '2.4em' }}>
                                    {building.modelType === '3d' ? '3D' : 'Image'}
                                </div>
                            }
                        />
                        <FormRow
                            key="buildingModelFile"
                            label="Current Model File"
                            content={
                                <Button variant="link p-0">View Model</Button>
                            }
                        />
                        <div className="form-check mt-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                name="modelType"
                                id="changeModel"
                                onChange={e => setChangeModel(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="changeModel">
                                Add new model
                            </label>
                        </div>
                        {!!changeModel &&
                            <div className="callout">
                                <p>
                                    This will replace the previous model when adding documentation records to this building.
                                </p>
                                <FormRow
                                    key="new3dModel"
                                    label={
                                        <span className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="newModelType"
                                                id="newModelType3d"
                                                checked={form.newModelType === '3d'}
                                                onChange={e => handleChange('newModelType', '3d')}
                                            />
                                            <label className="form-check-label" htmlFor="newModelType3d">
                                                3D Model <small className="fw-light">(GLB)</small>
                                            </label>
                                        </span>
                                    }
                                    content={
                                        <div className="mt-1 mb-3">
                                            <input
                                                type="file"
                                                disabled={form.newModelType !== '3d'}
                                                onChange={(event) => {
                                                    const file = event.target.files[0]
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                        handleChange('newModelFile', {
                                                            uri: reader.result,
                                                            name: file.name
                                                        })
                                                    };
                                                    reader.readAsDataURL(file)
                                                }}
                                            />
                                        </div>
                                    }
                                />
                                <FormRow
                                    key="new2dModel"
                                    label={
                                        <span className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="newModelType"
                                                id="newModelType2d"
                                                checked={form.newModelType === '2d'}
                                                onChange={e => handleChange('newModelType', '2d')}
                                            />
                                            <label className="form-check-label" htmlFor="newModelType2d">
                                                Image <small className="fw-light">(JPG or PNG)</small>
                                            </label>
                                        </span>
                                    }
                                    content={
                                        <div className={`mt-1 mb-3 ${form.newModelType !== '2d' ? 'disabled' : ''}`}>
                                            <ReactImagePickerEditor
                                                config={{
                                                    borderRadius: '8px',
                                                    language: 'en',
                                                    width: '330px',
                                                    height: '250px',
                                                    objectFit: 'contain',
                                                    compressInitial: null,
                                                }}
                                                imageChanged={(dataUri) => {
                                                    handleChange('newModelFile', {
                                                        uri: dataUri
                                                    })
                                                }}
                                            />
                                        </div>
                                    }
                                />
                            </div>
                        }
                        {errorMessage && 
                            <Alert key='submitError' variant='danger'>
                                {errorMessage}
                            </Alert>
                        }
                        <div className="mt-5 mb-4 d-grid d-lg-block">
                            <CancelButton onClick={cancel} />
                            <SaveButton isSubmitting={isSubmitting} onClick={handleSubmit} title="Save" />
                        </div>
                    </>
                }
                {errorMessage && 
                    <Alert key='submitError' variant='danger'>
                        {errorMessage}
                    </Alert>
                }
                <Footer user={currentUser}  />
            </main>
        </>
    )
}

export default BuildingNew